import { AllDocumentTypes } from 'types';

import { api } from './api';
import { createSSECacheHandler } from './createSSECacheHandler';

interface DocumentPullRequest {
  accountIds?: string[];
  trancheName?: string;
  fundKey?: string;
  format: 'zip' | 'pdf';
  reportName: string;
  selectedDocumentTypes: AllDocumentTypes[];
  fileNameTokens: string[];
}

interface DocumentPullResponse {
  requests: { id: string; status: string; accountCount: number }[];
}

export interface DocumentPullReport {
  id: string;
  reportName: string;
  format: string;
  creatorName: string;
  creatorEmail: string;
  createdAt: string;
  status: string;
  progress?: number;
  processedAccounts?: number;
  accountCount: number;
  successfulAccountCount?: number;
  downloadSizeBytes?: number;
  completedAt?: string;
  deliveredAt?: string;
  error?: string;
  errorCount: number;
  warningCount: number;
  trancheName?: string;
  fundKey?: string;
}

interface DocumentPullReportListResponse {
  reports: DocumentPullReport[];
  total: number;
}

interface DocumentPullReportListParams {
  limit?: number;
  offset?: number;
  status?: string;
}

interface DocumentPullProgressMessage {
  requestId: string;
  progress: number;
  processedAccounts: number;
  accountCount: number;
  status: string;
  errorCount?: number;
  warningCount?: number;
}

/**
 * Applies a progress SSE message to the cached report list. Chunk jobs publish progress from many
 * workers, so messages can arrive out of order; while a pull is still in flight we clamp progress
 * and processedAccounts to be monotonic so the bar never jumps backward. Terminal messages
 * (completed / completedWithWarning / failed) set the values directly so the bar can settle.
 */
export const applyDocumentPullProgress = (
  draft: DocumentPullReportListResponse,
  message: DocumentPullProgressMessage,
): void => {
  const report = draft.reports.find((r) => r.id === message.requestId);
  if (!report) return;

  const isTerminal =
    message.status === 'completed' || message.status === 'completedWithWarning' || message.status === 'failed';

  report.progress = isTerminal ? message.progress : Math.max(report.progress ?? 0, message.progress);
  report.processedAccounts = isTerminal
    ? message.processedAccounts
    : Math.max(report.processedAccounts ?? 0, message.processedAccounts);
  report.status = message.status;
  if (message.errorCount !== undefined) report.errorCount = message.errorCount;
  if (message.warningCount !== undefined) report.warningCount = message.warningCount;
};

export const documentPullApi = api.injectEndpoints({
  endpoints: (build) => ({
    createDocumentPull: build.mutation<DocumentPullResponse, DocumentPullRequest>({
      query: (body) => ({
        url: 'document-pull',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DocumentPullReports'],
    }),
    getDocumentPullReports: build.query<DocumentPullReportListResponse, DocumentPullReportListParams>({
      query: (params) => ({
        url: 'document-pull',
        params,
      }),
      providesTags: ['DocumentPullReports'],
      onCacheEntryAdded: createSSECacheHandler<DocumentPullReportListResponse, DocumentPullProgressMessage>({
        getSSEUrl: (data) => {
          const activeIds = data.reports
            .filter((r) => r.status === 'pending' || r.status === 'running')
            .map((r) => r.id);
          return activeIds.length > 0
            ? `/api/document-pull/progress?requestIds=${encodeURIComponent(activeIds.join(','))}`
            : null;
        },
        updateCache: applyDocumentPullProgress,
      }),
    }),
  }),
});

export const { useCreateDocumentPullMutation, useGetDocumentPullReportsQuery } = documentPullApi;
